/**
 * Student Data Normalizer
 * Handles business-layer normalization of student data received from Bridge
 */

import type { Student } from '@/services/bridge/types/bridge.types'

/**
 * Collapse undefined / null / empty / whitespace-only string to null.
 */
const normalizeNullableString = (
  value: string | undefined | null
): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export const normalizeSeatNumber = (
  seatNumber: string | undefined | null
): string | null => normalizeNullableString(seatNumber)

export const normalizeStudentId = (
  studentId: string | undefined | null
): string | null => normalizeNullableString(studentId)

/**
 * Validate and coerce serialNumber.
 * - Missing → null (legacy Windows host case)
 * - Present but not a number → throw (data corruption)
 */
const normalizeSerialNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(
      `[studentDataNormalizer] serialNumber must be a finite number, got ${typeof value}: ${String(value)}`
    )
  }
  return value
}

/**
 * Normalize a single student.
 *
 * Enforces invariant: studentId 與 serialNumber 不會同時為 null.
 * 缺 serialNumber 屬於舊版 host 情境 — 此時 studentId 必為非空 string。
 */
export const normalizeStudentData = (student: Student): Student => {
  const studentId = normalizeStudentId(student.studentId)
  const serialNumber = normalizeSerialNumber(student.serialNumber)
  const seatNumber = normalizeSeatNumber(student.seatNumber)

  if (studentId === null && serialNumber === null) {
    throw new Error(
      `[studentDataNormalizer] Student "${student.name}" has neither studentId nor serialNumber — at least one is required.`
    )
  }

  return {
    ...student,
    studentId,
    seatNumber,
    serialNumber,
  }
}

/**
 * Normalize a list of students
 */
export const normalizeStudentList = (students: Student[]): Student[] => {
  return students.map(normalizeStudentData)
}
